import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { prizeService, authService } from '../services';
import BackButton from '../components/BackButton';
import { MovieTicketTextAmount } from '../components/MovieTicketIcon';

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
const getImageUrl = (url) => {
  if (!url) return 'https://via.placeholder.com/300x200/9b59b6/ffffff?text=Prize';
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_ORIGIN}${url}`;
};

const CURRENCY_LABELS = {
  points: '积分',
  movie_ticket: '电影票'
};

const CURRENCY_BALANCE_KEYS = {
  points: 'points',
  movie_ticket: 'movie_tickets'
};

const CURRENCY_TYPES = ['points', 'movie_ticket'];

const getPrizeImages = (prize) => (
  prize.images?.length ? prize.images : [{ image_url: prize.image_url || prize.prize_image }]
);

const getPrizeForSelectedOptionImage = (prize, option) => {
  if (!prize || !option?.image_url) return prize;
  return {
    ...prize,
    image_url: option.image_url,
    images: [{
      id: `option-${option.id}-image`,
      image_url: option.image_url,
      original_name: option.name || prize.name
    }],
    auto_carousel: false
  };
};

const isAutoCarouselEnabled = (prize) => {
  if (prize.auto_carousel === false) return false;
  const value = String(prize.auto_carousel);
  return value !== '0' && value !== 'false';
};

const getActiveOptions = (prize) => (
  (prize?.options || []).filter((option) => option.is_active !== 0 && option.is_deleted !== 1)
);

const getUnitCost = (prize, option, currencyType) => {
  if (option) {
    const optionValue = currencyType === 'movie_ticket' ? option.movie_ticket_cost : option.cost;
    if (optionValue !== null && optionValue !== undefined && optionValue !== '') return Number(optionValue);
  }
  const prizeValue = currencyType === 'movie_ticket' ? prize.movie_ticket_cost : prize.cost;
  if (prizeValue === null || prizeValue === undefined || prizeValue === '') return null;
  return Number(prizeValue);
};

const getAvailableCurrencies = (prize, option = null) => {
  if (!prize) return [];
  const options = getActiveOptions(prize);
  if (options.length > 0 && !option) return [];

  return CURRENCY_TYPES
    .map((type) => ({ type, label: CURRENCY_LABELS[type], cost: getUnitCost(prize, option, type) }))
    .filter((currency) => currency.cost !== null && Number.isFinite(currency.cost));
};

const getDisplayCurrencies = (prize) => {
  const options = getActiveOptions(prize);
  const sources = options.length ? options : [null];
  return CURRENCY_TYPES
    .map((type) => {
      const costs = sources
        .map((option) => getUnitCost(prize, option, type))
        .filter((value) => value !== null && Number.isFinite(value));
      if (!costs.length) return null;
      return { type, label: CURRENCY_LABELS[type], min: Math.min(...costs), max: Math.max(...costs) };
    })
    .filter(Boolean);
};

const getPrizeStock = (prize, option = null) => {
  if (!prize) return 0;
  if (option) return Number(option.stock || 0);
  return Number(prize.available_stock ?? prize.stock ?? 0);
};

const getCurrencyBalance = (user, currencyType) => Number(user?.[CURRENCY_BALANCE_KEYS[currencyType]] || 0);

const formatCurrencyText = (currency, amount) => (
  currency?.type === 'movie_ticket' ? `${amount} 电影票` : `${amount} ${currency?.label || ''}`
);

const deliveryLabel = (type) => (type === 'virtual' ? '虚拟商品' : '实体商品');
const isPhysicalDelivery = (type) => type !== 'virtual';
const getEmptyAddressForm = () => ({
  recipient_name: '',
  phone: '',
  region: '',
  detail_address: '',
  is_default: false
});

function CurrencyAmount({ currency, amount }) {
  if (!currency) return null;
  if (currency.type === 'movie_ticket') return <MovieTicketTextAmount amount={amount} />;
  return <span>{amount} {currency.label}</span>;
}

const formatCardPrice = (currency) => {
  const amount = currency.min === currency.max ? currency.min : `${currency.min}-${currency.max}`;
  return `${amount} ${currency.label}`;
};

function PrizeCardPrice({ currencies }) {
  if (!currencies.length) return <span className="prize-card-price-unavailable">未开放兑换</span>;
  return (
    <>
      {currencies.map((currency, index) => (
        <React.Fragment key={currency.type}>
          {index > 0 && <span className="prize-card-price-or">或</span>}
          <span className="prize-card-price-pill">{formatCardPrice(currency)}</span>
        </React.Fragment>
      ))}
    </>
  );
}

function CurrencyUnitList({ currencies, emptyText = '未开放兑换' }) {
  if (!currencies.length) return <span>{emptyText}</span>;
  return (
    <>
      {currencies.map((currency, index) => (
        <React.Fragment key={currency.type}>
          {index > 0 && <span className="prize-price-or">或</span>}
          <span className="prize-price-unit">
            <CurrencyAmount currency={currency} amount={currency.cost} />
          </span>
        </React.Fragment>
      ))}
    </>
  );
}

function PrizeImageGallery({ prize, activeIndex, onSelect, onPreview, interactive = true }) {
  const images = getPrizeImages(prize);
  const normalizedIndex = Math.min(activeIndex || 0, images.length - 1);
  const hasMultipleImages = images.length > 1;

  const goToImage = (nextIndex) => {
    onSelect((nextIndex + images.length) % images.length);
  };

  return (
    <>
      <div className={`prize-image-gallery ${interactive ? '' : 'prize-image-gallery-static'}`}>
        <div className="prize-gallery-track" style={{ transform: `translateX(-${normalizedIndex * 100}%)` }}>
          {images.map((image, index) => {
            const GallerySlide = interactive ? 'button' : 'div';
            return (
              <GallerySlide
                key={image.id || `${prize.id || prize.prize_id}-image-${index}`}
                type={interactive ? 'button' : undefined}
                className={`prize-gallery-preview-trigger ${interactive ? '' : 'static'}`}
                aria-label={interactive ? '放大查看礼物图片' : undefined}
                onClick={interactive ? () => onPreview(index) : undefined}
              >
                <img src={getImageUrl(image.image_url)} alt={prize.name || prize.prize_name} className="prize-gallery-image" />
              </GallerySlide>
            );
          })}
        </div>

        {interactive && hasMultipleImages && (
          <>
            <button type="button" className="prize-gallery-nav prize-gallery-nav-prev" aria-label="上一张礼物图片" onClick={() => goToImage(normalizedIndex - 1)}>‹</button>
            <button type="button" className="prize-gallery-nav prize-gallery-nav-next" aria-label="下一张礼物图片" onClick={() => goToImage(normalizedIndex + 1)}>›</button>
          </>
        )}
      </div>

      {interactive && hasMultipleImages && (
        <div className="prize-gallery-dots" aria-label="礼物图片分页">
          {images.map((image, index) => (
            <button
              key={image.id || `${prize.id || prize.prize_id}-dot-${index}`}
              type="button"
              className={`prize-gallery-dot ${index === normalizedIndex ? 'active' : ''}`}
              aria-label={`查看第 ${index + 1} 张礼物图片`}
              aria-current={index === normalizedIndex ? 'true' : undefined}
              onClick={() => goToImage(index)}
            />
          ))}
        </div>
      )}
    </>
  );
}

function QuantityControl({ value, max, disabled, onChange }) {
  const normalizedMax = Math.max(1, Number(max || 1));
  const setQuantity = (nextValue) => {
    const parsed = Number.parseInt(nextValue, 10);
    const next = Number.isFinite(parsed) ? Math.min(normalizedMax, Math.max(1, parsed)) : 1;
    onChange(next);
  };

  return (
    <div className="quantity-control">
      <button type="button" disabled={disabled || value <= 1} onClick={() => setQuantity(value - 1)}>-</button>
      <input type="number" min="1" max={normalizedMax} value={value} disabled={disabled} onChange={(event) => setQuantity(event.target.value)} />
      <button type="button" disabled={disabled || value >= normalizedMax} onClick={() => setQuantity(value + 1)}>+</button>
    </div>
  );
}

function PrizesWithCart() {
  const [prizes, setPrizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [redeeming, setRedeeming] = useState(null);
  const [cartLoading, setCartLoading] = useState(false);
  const [cartCheckingOut, setCartCheckingOut] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [cartClosing, setCartClosing] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [galleryIndexes, setGalleryIndexes] = useState({});
  const [previewImage, setPreviewImage] = useState(null);
  const cartCloseTimerRef = useRef(null);
  const [redeemPrize, setRedeemPrize] = useState(null);
  const [selectedOptionId, setSelectedOptionId] = useState('');
  const [selectedCurrencyType, setSelectedCurrencyType] = useState('');
  const [redeemQuantity, setRedeemQuantity] = useState(1);
  const [modalError, setModalError] = useState('');
  const isAuthenticated = authService.isAuthenticated();
  const [currentUser, setCurrentUser] = useState(authService.getCurrentUser());
  const [addresses, setAddresses] = useState([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressSaving, setAddressSaving] = useState(false);
  const [addressForm, setAddressForm] = useState(getEmptyAddressForm());
  const [addressFormVisible, setAddressFormVisible] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [checkout, setCheckout] = useState(null);
  const [checkoutAddressId, setCheckoutAddressId] = useState('');
  const [checkoutRemarks, setCheckoutRemarks] = useState({});
  const [checkoutError, setCheckoutError] = useState('');

  const setGalleryIndex = useCallback((prizeId, imageCount, nextIndex) => {
    setGalleryIndexes((current) => ({ ...current, [prizeId]: (nextIndex + imageCount) % imageCount }));
  }, []);

  useEffect(() => {
    if (!prizes.some((prize) => isAutoCarouselEnabled(prize) && getPrizeImages(prize).length > 1)) return undefined;
    const timer = setInterval(() => {
      setGalleryIndexes((current) => {
        const next = { ...current };
        prizes.forEach((prize) => {
          const images = getPrizeImages(prize);
          if (isAutoCarouselEnabled(prize) && images.length > 1) {
            next[prize.id] = ((current[prize.id] || 0) + 1) % images.length;
          }
        });
        return next;
      });
    }, 4000);
    return () => clearInterval(timer);
  }, [prizes]);

  const updatePreviewIndex = useCallback((direction) => {
    if (!previewImage) return;
    const images = previewImage.images || getPrizeImages(prizes.find((item) => String(item.id) === String(previewImage.prizeId)) || {});
    if (images.length <= 1) return;
    const nextIndex = (previewImage.index + direction + images.length) % images.length;
    setPreviewImage({ ...previewImage, index: nextIndex });
    setGalleryIndex(previewImage.prizeId, images.length, nextIndex);
  }, [previewImage, prizes, setGalleryIndex]);

  useEffect(() => {
    if (!previewImage) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setPreviewImage(null);
      if (event.key === 'ArrowLeft') updatePreviewIndex(-1);
      if (event.key === 'ArrowRight') updatePreviewIndex(1);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [previewImage, updatePreviewIndex]);

  const previewPrize = previewImage ? prizes.find((prize) => String(prize.id) === String(previewImage.prizeId)) : null;
  const previewImages = previewImage?.images || (previewPrize ? getPrizeImages(previewPrize) : []);
  const previewIndex = previewImages.length ? Math.min(previewImage?.index || 0, previewImages.length - 1) : 0;
  const previewImageItem = previewImages[previewIndex];
  const previewImageUrl = previewImageItem ? getImageUrl(previewImageItem.image_url) : '';
  const hasPreviewMultipleImages = previewImages.length > 1;

  const loadPrizes = useCallback(async () => {
    try {
      const data = await prizeService.getAllPrizes();
      setPrizes(data);
    } catch (err) {
      setError('加载奖品失败');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCurrentUser = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const profile = await authService.getProfile();
      const merged = { ...(authService.getCurrentUser() || {}), ...profile };
      authService.setCurrentUser(merged);
      setCurrentUser(merged);
    } catch (err) {
      setCurrentUser(authService.getCurrentUser());
    }
  }, [isAuthenticated]);

  const loadCart = useCallback(async () => {
    if (!isAuthenticated) return;
    setCartLoading(true);
    try {
      const data = await prizeService.getCart();
      setCartItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.message || '加载购物车失败');
    } finally {
      setCartLoading(false);
    }
  }, [isAuthenticated]);

  const loadAddresses = useCallback(async () => {
    if (!isAuthenticated) return [];
    setAddressLoading(true);
    try {
      const data = await prizeService.getShippingAddresses();
      const list = Array.isArray(data) ? data : [];
      setAddresses(list);
      return list;
    } catch (err) {
      setCheckoutError(err.response?.data?.message || '加载收货地址失败');
      return [];
    } finally {
      setAddressLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadPrizes();
    loadCurrentUser();
    loadCart();
  }, [loadPrizes, loadCurrentUser, loadCart]);

  useEffect(() => () => {
    if (cartCloseTimerRef.current) {
      clearTimeout(cartCloseTimerRef.current);
    }
  }, []);

  const openCart = () => {
    if (cartCloseTimerRef.current) {
      clearTimeout(cartCloseTimerRef.current);
      cartCloseTimerRef.current = null;
    }
    setCartClosing(false);
    setCartOpen(true);
  };

  const closeCart = () => {
    if (!cartOpen || cartClosing) return;
    setCartClosing(true);
    cartCloseTimerRef.current = setTimeout(() => {
      setCartOpen(false);
      setCartClosing(false);
      cartCloseTimerRef.current = null;
    }, 220);
  };

  const applyRemainingBalances = (remainingBalances = {}) => {
    const user = authService.getCurrentUser();
    if (!user) return;
    const updatedUser = { ...user };
    if (remainingBalances.points !== undefined) updatedUser.points = remainingBalances.points;
    if (remainingBalances.movie_ticket !== undefined) updatedUser.movie_tickets = remainingBalances.movie_ticket;
    authService.setCurrentUser(updatedUser);
    setCurrentUser(updatedUser);
  };

  const selectedOption = redeemPrize ? getActiveOptions(redeemPrize).find((option) => String(option.id) === String(selectedOptionId)) : null;
  const redeemGalleryPrize = redeemPrize ? getPrizeForSelectedOptionImage(redeemPrize, selectedOption) : null;
  const redeemOptions = redeemPrize ? getActiveOptions(redeemPrize) : [];
  const redeemStock = redeemPrize ? getPrizeStock(redeemPrize, selectedOption) : 0;
  const redeemCurrencies = redeemPrize ? getAvailableCurrencies(redeemPrize, selectedOption) : [];
  const selectedCurrency = redeemCurrencies.find((currency) => currency.type === selectedCurrencyType);
  const selectedCurrencyTotal = selectedCurrency ? selectedCurrency.cost * redeemQuantity : 0;
  const selectedCurrencyBalance = selectedCurrency ? getCurrencyBalance(currentUser, selectedCurrency.type) : 0;
  const needsOptionSelection = redeemOptions.length > 0 && !selectedOption;
  const isSelectedCurrencyInsufficient = selectedCurrency ? selectedCurrencyBalance < selectedCurrencyTotal : false;
  const redeemKey = redeemPrize && selectedCurrency ? `${redeemPrize.id}:${selectedOption?.id || 'base'}:${selectedCurrency.type}` : '';
  const isRedeemConfirmDisabled = !selectedCurrency || needsOptionSelection || isSelectedCurrencyInsufficient || redeemQuantity < 1 || redeemQuantity > redeemStock || Boolean(redeeming);

  const openRedeemModal = (prize) => {
    if (!isAuthenticated) {
      setError('请先登录以兑换奖品');
      return;
    }
    if (getPrizeStock(prize) <= 0) return;
    const options = getActiveOptions(prize);
    const initialOption = options.length === 1 ? options[0] : null;
    setRedeemPrize(prize);
    setSelectedOptionId(initialOption ? String(initialOption.id) : '');
    setSelectedCurrencyType('');
    setRedeemQuantity(1);
    setModalError('');
    setError('');
    setSuccess('');
  };

  const closeRedeemModal = (force = false) => {
    if (!force && redeeming) return;
    setRedeemPrize(null);
    setSelectedOptionId('');
    setSelectedCurrencyType('');
    setRedeemQuantity(1);
    setModalError('');
  };

  const handleOptionSelect = (optionId) => {
    setSelectedOptionId(optionId);
    setSelectedCurrencyType('');
    setRedeemQuantity(1);
    setModalError('');
  };

  const handleCurrencySelect = (currency) => {
    if (!redeemPrize || redeemStock <= 0 || redeeming || needsOptionSelection) return;
    if (getCurrencyBalance(currentUser, currency.type) < currency.cost * redeemQuantity) return;
    setSelectedCurrencyType(currency.type);
    setModalError('');
  };

  const openCheckoutModal = async (mode, items, directPayload = null) => {
    const normalizedItems = items.map((item) => ({
      ...item,
      key: String(item.key || item.id)
    }));
    const needsAddress = normalizedItems.some((item) => isPhysicalDelivery(item.delivery_type));
    setCheckout({ mode, items: normalizedItems, directPayload });
    setCheckoutRemarks(normalizedItems.reduce((acc, item) => ({ ...acc, [item.key]: '' }), {}));
    setCheckoutError('');
    setAddressForm(getEmptyAddressForm());
    setAddressFormVisible(false);
    setEditingAddressId(null);

    if (needsAddress) {
      const list = addresses.length ? addresses : await loadAddresses();
      const selectedAddress = list.find((address) => address.is_default) || list[0];
      setCheckoutAddressId(selectedAddress ? String(selectedAddress.id) : '');
    } else {
      setCheckoutAddressId('');
    }
  };

  const openDirectCheckout = () => {
    if (!redeemPrize || isRedeemConfirmDisabled) return;
    const item = {
      key: 'direct',
      id: 'direct',
      prize_id: redeemPrize.id,
      prize_option_id: selectedOption ? selectedOption.id : null,
      prize_name: redeemPrize.name,
      option_name: selectedOption ? selectedOption.name : '',
      prize_image: selectedOption?.image_url || redeemPrize.image_url || redeemPrize.prize_image,
      delivery_type: redeemPrize.delivery_type || 'physical',
      quantity: redeemQuantity,
      currency_type: selectedCurrency.type,
      unit_currency_cost: selectedCurrency.cost,
      currency_cost: selectedCurrencyTotal,
      is_available: true
    };
    openCheckoutModal('direct', [item], {
      prizeId: redeemPrize.id,
      currencyType: selectedCurrency.type,
      prizeOptionId: selectedOption ? selectedOption.id : null,
      quantity: redeemQuantity
    });
  };

  const closeCheckoutModal = (force = false) => {
    if (!force && (cartCheckingOut || redeeming)) return;
    setCheckout(null);
    setCheckoutError('');
    setCheckoutAddressId('');
    setCheckoutRemarks({});
    setAddressForm(getEmptyAddressForm());
    setAddressFormVisible(false);
    setEditingAddressId(null);
  };

  const startAddressCreate = () => {
    setEditingAddressId(null);
    setAddressForm(getEmptyAddressForm());
    setAddressFormVisible(true);
    setCheckoutError('');
  };

  const startAddressEdit = (address) => {
    setEditingAddressId(address.id);
    setAddressFormVisible(true);
    setAddressForm({
      recipient_name: address.recipient_name || '',
      phone: address.phone || '',
      region: address.region || '',
      detail_address: address.detail_address || '',
      is_default: Boolean(address.is_default)
    });
    setCheckoutError('');
  };

  const saveAddress = async (event) => {
    event.preventDefault();
    setAddressSaving(true);
    setCheckoutError('');
    try {
      const saved = editingAddressId
        ? await prizeService.updateShippingAddress(editingAddressId, addressForm)
        : await prizeService.createShippingAddress(addressForm);
      const list = await loadAddresses();
      setCheckoutAddressId(String(saved?.id || editingAddressId || list.find((address) => address.is_default)?.id || ''));
      setAddressForm(getEmptyAddressForm());
      setAddressFormVisible(false);
      setEditingAddressId(null);
    } catch (err) {
      setCheckoutError(err.response?.data?.message || '保存收货地址失败');
    } finally {
      setAddressSaving(false);
    }
  };

  const deleteAddress = async (addressId) => {
    setAddressSaving(true);
    setCheckoutError('');
    try {
      await prizeService.deleteShippingAddress(addressId);
      const list = await loadAddresses();
      if (String(addressId) === String(checkoutAddressId)) {
        const nextAddress = list.find((address) => address.is_default) || list[0];
        setCheckoutAddressId(nextAddress ? String(nextAddress.id) : '');
      }
    } catch (err) {
      setCheckoutError(err.response?.data?.message || '删除收货地址失败');
    } finally {
      setAddressSaving(false);
    }
  };

  const setDefaultAddress = async (addressId) => {
    setAddressSaving(true);
    setCheckoutError('');
    try {
      await prizeService.setDefaultShippingAddress(addressId);
      await loadAddresses();
      setCheckoutAddressId(String(addressId));
    } catch (err) {
      setCheckoutError(err.response?.data?.message || '设置默认地址失败');
    } finally {
      setAddressSaving(false);
    }
  };

  const handleAddToCart = async () => {
    if (!redeemPrize || isRedeemConfirmDisabled) return;
    setRedeeming(`cart:${redeemKey}`);
    setModalError('');
    setError('');
    setSuccess('');
    try {
      const nextCart = await prizeService.addCartItem({
        prizeId: redeemPrize.id,
        prize_option_id: selectedOption ? selectedOption.id : null,
        currency_type: selectedCurrency.type,
        quantity: redeemQuantity
      });
      setCartItems(Array.isArray(nextCart) ? nextCart : []);
      setSuccess(`已加入购物车：「${redeemPrize.name}」x${redeemQuantity}`);
      openCart();
      closeRedeemModal();
    } catch (err) {
      const message = err.response?.data?.message || '加入购物车失败';
      setModalError(message);
      setError(message);
    } finally {
      setRedeeming(null);
    }
  };

  const handleConfirmRedeem = async () => {
    openDirectCheckout();
  };

  const updateCartItem = async (itemId, data) => {
    setCartLoading(true);
    setError('');
    setSuccess('');
    try {
      const nextCart = await prizeService.updateCartItem(itemId, data);
      setCartItems(Array.isArray(nextCart) ? nextCart : []);
    } catch (err) {
      setError(err.response?.data?.message || '更新购物车失败');
    } finally {
      setCartLoading(false);
    }
  };

  const removeCartItem = async (itemId) => {
    setCartLoading(true);
    setError('');
    setSuccess('');
    try {
      const nextCart = await prizeService.deleteCartItem(itemId);
      setCartItems(Array.isArray(nextCart) ? nextCart : []);
    } catch (err) {
      setError(err.response?.data?.message || '移除购物车项失败');
    } finally {
      setCartLoading(false);
    }
  };

  const clearCart = async () => {
    setCartLoading(true);
    setError('');
    setSuccess('');
    try {
      await prizeService.clearCart();
      setCartItems([]);
    } catch (err) {
      setError(err.response?.data?.message || '清空购物车失败');
    } finally {
      setCartLoading(false);
    }
  };

  const cartTotals = useMemo(() => cartItems.reduce((acc, item) => {
    if (!item.is_available || item.currency_cost === null || item.currency_cost === undefined) return acc;
    acc[item.currency_type] = (acc[item.currency_type] || 0) + Number(item.currency_cost || 0);
    return acc;
  }, {}), [cartItems]);
  const cartHasUnavailable = cartItems.some((item) => !item.is_available);
  const cartInsufficientCurrency = Object.entries(cartTotals).find(([currencyType, total]) => getCurrencyBalance(currentUser, currencyType) < total);
  const cartCount = cartItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  const isCheckoutDisabled = cartItems.length === 0 || cartHasUnavailable || Boolean(cartInsufficientCurrency) || cartCheckingOut || cartLoading;

  const checkoutCart = async () => {
    if (isCheckoutDisabled) return;
    openCheckoutModal('cart', cartItems);
  };

  const checkoutItems = checkout?.items || [];
  const checkoutHasPhysical = checkoutItems.some((item) => isPhysicalDelivery(item.delivery_type));
  const checkoutTotals = checkoutItems.reduce((acc, item) => {
    if (item.currency_cost === null || item.currency_cost === undefined) return acc;
    acc[item.currency_type] = (acc[item.currency_type] || 0) + Number(item.currency_cost || 0);
    return acc;
  }, {});
  const isCheckoutSubmitDisabled = !checkout
    || cartCheckingOut
    || Boolean(redeeming)
    || (checkoutHasPhysical && !checkoutAddressId);

  const submitCheckout = async () => {
    if (isCheckoutSubmitDisabled) {
      if (checkoutHasPhysical && !checkoutAddressId) setCheckoutError('请选择收货地址');
      return;
    }

    setCartCheckingOut(true);
    setCheckoutError('');
    setError('');
    setSuccess('');
    try {
      if (checkout.mode === 'direct') {
        const item = checkoutItems[0];
        const directKey = `direct:${item.prize_id}:${item.prize_option_id || 'base'}:${item.currency_type}`;
        setRedeeming(directKey);
        const result = await prizeService.redeemPrize(checkout.directPayload.prizeId, {
          ...checkout.directPayload,
          addressId: checkoutHasPhysical ? checkoutAddressId : null,
          remark: checkoutRemarks[item.key] || ''
        });
        applyRemainingBalances({ [result.currency_type]: result.remainingBalance ?? result.remainingPoints });
        setSuccess(`成功兑换 "${item.prize_name}" x${item.quantity}！剩余${CURRENCY_LABELS[item.currency_type]}：${result.remainingBalance ?? result.remainingPoints}`);
        closeCheckoutModal(true);
        closeRedeemModal(true);
      } else {
        const result = await prizeService.checkoutCart({
          address_id: checkoutHasPhysical ? checkoutAddressId : null,
          remarks: checkoutRemarks
        });
        applyRemainingBalances(result.remainingBalances || {});
        setSuccess(`购物车结算成功，共生成 ${result.redemptions?.length || 0} 条兑换记录`);
        setCartItems([]);
        closeCheckoutModal(true);
        closeCart();
      }
      loadPrizes();
      loadCurrentUser();
    } catch (err) {
      const message = err.response?.data?.message || (checkout.mode === 'direct' ? '兑换失败' : '购物车结算失败');
      setCheckoutError(message);
      setError(message);
      if (checkout.mode === 'cart') loadCart();
    } finally {
      setRedeeming(null);
      setCartCheckingOut(false);
    }
  };

  if (loading) return <div className="loading">正在加载奖品...</div>;

  return (
    <div className="container">
      <BackButton to="/" />
      <div className="prizes-page-header">
        <h1 className="page-title">兑换商城</h1>
        <p className="page-subtitle">用积分或电影票兑换专属礼物吧！</p>
      </div>

      {isAuthenticated && (
        <button type="button" className="prize-cart-fab" onClick={openCart} aria-label={`打开购物车${cartCount > 0 ? `，共 ${cartCount} 件` : ''}`}>
          <svg className="prize-cart-fab-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M7.2 18.4a1.6 1.6 0 1 0 0 3.2 1.6 1.6 0 0 0 0-3.2Zm10 0a1.6 1.6 0 1 0 0 3.2 1.6 1.6 0 0 0 0-3.2ZM4.8 4.4H2.5a1 1 0 0 1 0-2h3.1c.5 0 .9.3 1 .8l.7 3.1h13.2c.3 0 .6.1.8.4.2.2.2.6.2.9l-1.4 6.2c-.3 1.4-1.5 2.4-2.9 2.4H8.7c-1.4 0-2.6-1-2.9-2.4L4.8 4.4Zm3 3.9 1 5.1c0 .4.4.8.9.8h7.5c.5 0 .8-.3.9-.8l1.1-5.1H7.8Z" />
          </svg>
          {cartCount > 0 && <span className="prize-cart-fab-count">{cartCount > 99 ? '99+' : cartCount}</span>}
        </button>
      )}

      {(error || success) && (
        <div className={`prize-notice ${error ? 'prize-notice-error' : 'prize-notice-success'}`} role={error ? 'alert' : 'status'}>
          <strong>{error ? '兑换失败' : '操作成功'}</strong>
          <span>{error || success}</span>
        </div>
      )}

      {prizes.length === 0 ? (
        <div className="empty-state"><div className="empty-state-icon">🎁</div><p>暂无奖品</p></div>
      ) : (
        <div className="grid">
          {prizes.map((prize) => {
            const currencies = getDisplayCurrencies(prize);
            const options = getActiveOptions(prize);
            const stock = getPrizeStock(prize);
            const redeemDisabled = stock <= 0 || !isAuthenticated || currencies.length === 0 || Boolean(redeeming);
            const openPrizeRedeem = () => {
              if (!redeemDisabled) openRedeemModal(prize);
            };
            return (
              <div
                key={prize.id}
                className={`card prize-card prize-card-clickable ${redeemDisabled ? 'prize-card-disabled' : ''}`}
                onClick={openPrizeRedeem}
              >
                <PrizeImageGallery prize={prize} activeIndex={galleryIndexes[prize.id]} onSelect={(nextIndex) => setGalleryIndex(prize.id, getPrizeImages(prize).length, nextIndex)} interactive={false} />
                <h3 className="card-title">{prize.name}</h3>
                <div className={`prize-delivery-chip ${prize.delivery_type === 'virtual' ? 'virtual' : 'physical'}`}>{deliveryLabel(prize.delivery_type)}</div>
                <div className="prize-card-footer">
                  <div className="prize-card-price-line">
                    <PrizeCardPrice currencies={currencies} />
                  </div>
                  <div className="prize-card-meta-line">
                    {options.length > 0 && <span>{options.length} 个规格</span>}
                    <span>{stock > 0 ? `库存 ${stock}` : '缺货'}</span>
                  </div>
                  <div className="prize-redeem-actions">
                    <button type="button" className="btn btn-primary" onClick={(event) => { event.stopPropagation(); openPrizeRedeem(); }} disabled={redeemDisabled}>
                      {stock <= 0 ? '已缺货' : currencies.length === 0 ? '未开放' : '兑换'}
                    </button>
                  </div>
                </div>
                {!isAuthenticated && <p style={{ color: 'var(--text-light)', fontSize: '0.875rem', marginTop: '0.5rem' }}>登录以兑换</p>}
              </div>
            );
          })}
        </div>
      )}

      {redeemPrize && (
        <div className="prize-redeem-modal" role="dialog" aria-modal="true" aria-labelledby="redeem-modal-title" onClick={closeRedeemModal}>
          <div className="prize-redeem-modal-content" onClick={(event) => event.stopPropagation()}>
            <button type="button" className="prize-redeem-modal-close" aria-label="关闭兑换窗口" onClick={closeRedeemModal} disabled={Boolean(redeeming)}>×</button>
            <div className="prize-redeem-modal-image">
              <PrizeImageGallery
                prize={redeemGalleryPrize}
                activeIndex={selectedOption?.image_url ? 0 : galleryIndexes[redeemPrize.id]}
                onSelect={(nextIndex) => setGalleryIndex(redeemPrize.id, getPrizeImages(redeemGalleryPrize).length, nextIndex)}
                onPreview={(index) => setPreviewImage({ prizeId: redeemPrize.id, index, images: getPrizeImages(redeemGalleryPrize) })}
              />
            </div>
            <div className="prize-redeem-modal-detail">
              <div className="prize-purchase-header">
                <p className="prize-redeem-eyebrow">礼物兑换</p>
                <h2 id="redeem-modal-title">{redeemPrize.name}</h2>
                <div className={`prize-delivery-chip inline ${redeemPrize.delivery_type === 'virtual' ? 'virtual' : 'physical'}`}>{deliveryLabel(redeemPrize.delivery_type)}</div>
                <div className="prize-price-hero" aria-label="商品单价">
                  {needsOptionSelection ? (
                    <div className="prize-price-hero-empty">
                      <span>单价</span>
                      <strong>请选择规格查看单价</strong>
                    </div>
                  ) : redeemCurrencies.length > 0 ? (
                    redeemCurrencies.map((currency, index) => (
                      <React.Fragment key={currency.type}>
                        {index > 0 && <span className="prize-price-hero-or">或</span>}
                        <div className="prize-price-hero-item">
                          <span>{currency.label}单价</span>
                          <strong><CurrencyAmount currency={currency} amount={currency.cost} /></strong>
                        </div>
                      </React.Fragment>
                    ))
                  ) : (
                    <div className="prize-price-hero-empty">
                      <span>单价</span>
                      <strong>暂未开放兑换</strong>
                    </div>
                  )}
                </div>
                <p className="prize-redeem-description">{redeemPrize.description || '暂无描述'}</p>
              </div>

              {redeemOptions.length > 0 && (
                <div className="prize-option-section">
                  <h3>选择规格</h3>
                  <div className="prize-option-list">
                    {redeemOptions.map((option) => {
                      const selected = String(option.id) === String(selectedOptionId);
                      const optionCurrencies = getAvailableCurrencies(redeemPrize, option);
                      const disabled = Number(option.stock || 0) <= 0 || !optionCurrencies.length || Boolean(redeeming);
                      return (
                        <button key={option.id} type="button" className={`prize-option-choice ${selected ? 'selected' : ''}`} disabled={disabled} onClick={() => handleOptionSelect(String(option.id))}>
                          <span>{option.name}</span>
                          <span className="prize-option-price-summary">
                            <CurrencyUnitList currencies={optionCurrencies} />
                          </span>
                          <small>{option.stock > 0 ? `库存 ${option.stock}` : '缺货'}</small>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="prize-purchase-row">
                <div className="prize-purchase-row-copy">
                  <span>数量</span>
                  <small>{needsOptionSelection ? '请选择规格' : redeemStock > 0 ? `库存 ${redeemStock}` : '缺货'}</small>
                </div>
                <QuantityControl value={redeemQuantity} max={redeemStock} disabled={needsOptionSelection || redeemStock <= 0 || Boolean(redeeming)} onChange={setRedeemQuantity} />
              </div>

              <div className="prize-payment-section">
                <h3>选择支付方式</h3>
                <div className="prize-payment-options">
                  {redeemCurrencies.map((currency) => {
                    const totalCost = currency.cost * redeemQuantity;
                    const balance = getCurrencyBalance(currentUser, currency.type);
                    const insufficient = balance < totalCost;
                    const isSelected = selectedCurrencyType === currency.type;
                    return (
                      <button key={currency.type} type="button" className={`prize-payment-option ${isSelected ? 'selected' : ''} ${insufficient ? 'disabled' : ''}`} onClick={() => handleCurrencySelect(currency)} disabled={insufficient || redeemStock <= 0 || Boolean(redeeming)} aria-pressed={isSelected}>
                        <span className="prize-payment-main">
                          <span className="prize-payment-name">{currency.label}</span>
                          <span className="prize-payment-formula">
                            <CurrencyAmount currency={currency} amount={currency.cost} /> × {redeemQuantity} = <CurrencyAmount currency={currency} amount={totalCost} />
                          </span>
                        </span>
                        <span className="prize-payment-meta">
                          <span className="prize-payment-cost">实付 <CurrencyAmount currency={currency} amount={totalCost} /></span>
                          <span className="prize-payment-balance">余额：<CurrencyAmount currency={currency} amount={balance} /></span>
                          <span className={`prize-payment-status ${insufficient ? 'insufficient' : ''}`}>{insufficient ? '余额不足' : isSelected ? '已选择' : '可选择'}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {modalError && <div className="prize-redeem-modal-error" role="alert">{modalError}</div>}

              <div className="prize-modal-actions">
                <button type="button" className="btn btn-secondary prize-redeem-confirm" onClick={handleAddToCart} disabled={isRedeemConfirmDisabled || Boolean(redeeming)}>
                  {redeeming === `cart:${redeemKey}` ? '加入中...' : '加入购物车'}
                </button>
                <button type="button" className="btn btn-primary prize-redeem-confirm" onClick={handleConfirmRedeem} disabled={isRedeemConfirmDisabled}>
                  {redeeming === `direct:${redeemKey}` ? '兑换中...' : selectedCurrency ? `直接兑换 ${formatCurrencyText(selectedCurrency, selectedCurrencyTotal)}` : '请选择支付方式'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {(cartOpen || cartClosing) && (
        <div className={`prize-cart-overlay ${cartClosing ? 'closing' : ''}`} role="dialog" aria-modal="true" aria-label="购物车" onClick={closeCart}>
          <aside className="prize-cart-panel" onClick={(event) => event.stopPropagation()}>
            <div className="prize-cart-header">
              <div><p className="prize-redeem-eyebrow">兑换商城</p><h2>购物车</h2></div>
              <button type="button" className="prize-redeem-modal-close" onClick={closeCart}>×</button>
            </div>
            {cartItems.length === 0 ? (
              <div className="empty-inline">购物车还是空的</div>
            ) : (
              <div className="prize-cart-list">
                {cartItems.map((item) => (
                  <div key={item.id} className={`prize-cart-item ${!item.is_available ? 'invalid' : ''}`}>
                    <img src={getImageUrl(item.prize_image)} alt={item.prize_name} />
                    <div className="prize-cart-item-main">
                      <div className="prize-cart-item-title">
                        <strong>{item.prize_name}</strong>
                        <span className={`prize-cart-delivery ${item.delivery_type === 'virtual' ? 'virtual' : 'physical'}`}>{deliveryLabel(item.delivery_type)}</span>
                        {item.option_name && <span>{item.option_name}</span>}
                      </div>
                      {!item.is_available && <div className="prize-cart-warning">{item.unavailable_reason}</div>}
                      <div className="prize-cart-controls">
                        <QuantityControl value={item.quantity} max={item.stock} disabled={cartLoading || !item.is_available} onChange={(quantity) => updateCartItem(item.id, { quantity })} />
                        <div className="prize-cart-currency-options">
                          {(item.available_currencies || []).map((currency) => (
                            <button key={currency.type} type="button" className={item.currency_type === currency.type ? 'selected' : ''} disabled={cartLoading} onClick={() => updateCartItem(item.id, { currency_type: currency.type })}>{currency.label}</button>
                          ))}
                        </div>
                      </div>
                      <div className="prize-cart-item-footer">
                        <span>小计：{item.unit_currency_cost === null ? '不可用' : <CurrencyAmount currency={{ type: item.currency_type, label: CURRENCY_LABELS[item.currency_type] }} amount={item.currency_cost} />}</span>
                        <button type="button" className="danger-text" onClick={() => removeCartItem(item.id)} disabled={cartLoading}>移除</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="prize-cart-summary">
              <div><span>积分合计</span><strong>{cartTotals.points || 0} 积分</strong></div>
              <div><span>电影票合计</span><strong>{cartTotals.movie_ticket || 0} 电影票</strong></div>
              {cartInsufficientCurrency && <p className="prize-cart-warning">{CURRENCY_LABELS[cartInsufficientCurrency[0]]}余额不足</p>}
            </div>
            <div className="prize-cart-actions">
              <button type="button" className="btn btn-outline" onClick={clearCart} disabled={cartLoading || cartItems.length === 0}>清空</button>
              <button type="button" className="btn btn-primary" onClick={checkoutCart} disabled={isCheckoutDisabled}>{cartCheckingOut ? '结算中...' : '结算购物车'}</button>
            </div>
          </aside>
        </div>
      )}

      {checkout && (
        <div className="prize-checkout-modal" role="dialog" aria-modal="true" aria-labelledby="checkout-modal-title" onClick={() => closeCheckoutModal()}>
          <div className="prize-checkout-modal-content" onClick={(event) => event.stopPropagation()}>
            <button type="button" className="prize-redeem-modal-close" aria-label="关闭结算窗口" onClick={() => closeCheckoutModal()} disabled={cartCheckingOut || Boolean(redeeming)}>×</button>
            <div className="prize-checkout-header">
              <p className="prize-redeem-eyebrow">兑换商城</p>
              <h2 id="checkout-modal-title">确认结算</h2>
              <span>{checkout.mode === 'cart' ? `${checkoutItems.length} 件商品` : '直接兑换'}</span>
            </div>

            <div className="prize-checkout-list">
              {checkoutItems.map((item) => (
                <div key={item.key} className="prize-checkout-item">
                  <img src={getImageUrl(item.prize_image)} alt={item.prize_name} />
                  <div className="prize-checkout-item-main">
                    <div>
                      <strong>{item.prize_name}</strong>
                      <span className={`prize-cart-delivery ${item.delivery_type === 'virtual' ? 'virtual' : 'physical'}`}>{deliveryLabel(item.delivery_type)}</span>
                      {item.option_name && <span className="prize-checkout-muted">{item.option_name}</span>}
                    </div>
                    <div className="prize-checkout-line-meta">
                      <span>x{item.quantity}</span>
                      <span><CurrencyAmount currency={{ type: item.currency_type, label: CURRENCY_LABELS[item.currency_type] }} amount={item.currency_cost} /></span>
                    </div>
                    <label className="prize-checkout-remark">
                      商品备注
                      <textarea
                        value={checkoutRemarks[item.key] || ''}
                        onChange={(event) => setCheckoutRemarks((current) => ({ ...current, [item.key]: event.target.value }))}
                        maxLength={500}
                        rows={2}
                        placeholder="可备注口味、尺寸、定制需求等"
                      />
                    </label>
                  </div>
                </div>
              ))}
            </div>

            {checkoutHasPhysical && (
              <section className="prize-address-section">
                <div className="prize-address-section-header">
                  <div>
                    <h3>收货地址</h3>
                    <p>实体商品需要选择收货地址，地址会保存供以后使用。</p>
                  </div>
                  <button type="button" className="btn btn-outline" onClick={startAddressCreate} disabled={addressSaving}>新增地址</button>
                </div>

                {addressLoading ? (
                  <div className="empty-inline">正在加载地址...</div>
                ) : addresses.length > 0 ? (
                  <div className="prize-address-list">
                    {addresses.map((address) => (
                      <label key={address.id} className={`prize-address-card ${String(checkoutAddressId) === String(address.id) ? 'selected' : ''}`}>
                        <input type="radio" name="checkout-address" checked={String(checkoutAddressId) === String(address.id)} onChange={() => setCheckoutAddressId(String(address.id))} />
                        <span>
                          <strong>{address.recipient_name} · {address.phone}</strong>
                          <small>{address.region}{address.detail_address}</small>
                          {address.is_default ? <em>默认地址</em> : null}
                        </span>
                        <span className="prize-address-actions">
                          {!address.is_default && <button type="button" onClick={() => setDefaultAddress(address.id)} disabled={addressSaving}>设默认</button>}
                          <button type="button" onClick={() => startAddressEdit(address)} disabled={addressSaving}>编辑</button>
                          <button type="button" className="danger-text" onClick={() => deleteAddress(address.id)} disabled={addressSaving}>删除</button>
                        </span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="empty-inline">还没有收货地址，请先新增一个。</div>
                )}

                {(addresses.length === 0 || addressFormVisible) && (
                  <form className="prize-address-form" onSubmit={saveAddress}>
                    <div className="prize-address-form-grid">
                      <label>
                        收货人
                        <input value={addressForm.recipient_name} onChange={(event) => setAddressForm({ ...addressForm, recipient_name: event.target.value })} required />
                      </label>
                      <label>
                        手机号
                        <input value={addressForm.phone} onChange={(event) => setAddressForm({ ...addressForm, phone: event.target.value })} required />
                      </label>
                      <label>
                        省市区
                        <input value={addressForm.region} onChange={(event) => setAddressForm({ ...addressForm, region: event.target.value })} placeholder="例如：上海市 浦东新区" required />
                      </label>
                      <label>
                        详细地址
                        <input value={addressForm.detail_address} onChange={(event) => setAddressForm({ ...addressForm, detail_address: event.target.value })} required />
                      </label>
                    </div>
                    <label className="toggle-line">
                      <input type="checkbox" checked={addressForm.is_default} onChange={(event) => setAddressForm({ ...addressForm, is_default: event.target.checked })} />
                      设为默认地址
                    </label>
                    <div className="prize-address-form-actions">
                      {addressFormVisible && addresses.length > 0 && <button type="button" className="btn btn-outline" onClick={() => { setAddressFormVisible(false); setEditingAddressId(null); setAddressForm(getEmptyAddressForm()); }} disabled={addressSaving}>取消</button>}
                      <button type="submit" className="btn btn-secondary" disabled={addressSaving}>{addressSaving ? '保存中...' : editingAddressId !== null ? '保存地址' : '新增地址'}</button>
                    </div>
                  </form>
                )}
              </section>
            )}

            <div className="prize-checkout-summary">
              <div><span>积分合计</span><strong>{checkoutTotals.points || 0} 积分</strong></div>
              <div><span>电影票合计</span><strong>{checkoutTotals.movie_ticket || 0} 电影票</strong></div>
              {checkoutHasPhysical && !checkoutAddressId && <p className="prize-cart-warning">请选择收货地址后再提交。</p>}
            </div>

            {checkoutError && <div className="prize-redeem-modal-error" role="alert">{checkoutError}</div>}

            <div className="prize-modal-actions">
              <button type="button" className="btn btn-outline prize-redeem-confirm" onClick={() => closeCheckoutModal()} disabled={cartCheckingOut || Boolean(redeeming)}>返回修改</button>
              <button type="button" className="btn btn-primary prize-redeem-confirm" onClick={submitCheckout} disabled={isCheckoutSubmitDisabled}>
                {cartCheckingOut || redeeming ? '提交中...' : '确认提交'}
              </button>
            </div>
          </div>
        </div>
      )}

      {previewImage && previewImageUrl && (
        <div className="prize-image-modal" role="dialog" aria-modal="true" onClick={() => setPreviewImage(null)}>
          <div className="prize-image-modal-content" onClick={(event) => event.stopPropagation()}>
            <button type="button" className="prize-image-modal-close" aria-label="关闭大图" onClick={() => setPreviewImage(null)}>×</button>
            {hasPreviewMultipleImages && <button type="button" className="prize-image-modal-nav prize-image-modal-nav-prev" aria-label="上一张礼物图片" onClick={() => updatePreviewIndex(-1)}>‹</button>}
            <div className="prize-image-modal-viewport">
              <div className="prize-image-modal-track" style={{ transform: `translateX(-${previewIndex * 100}%)` }}>
                {previewImages.map((image, index) => (
                  <div key={image.id || `preview-${index}`} className="prize-image-modal-slide"><img src={getImageUrl(image.image_url)} alt={previewPrize?.name || '礼物图片'} /></div>
                ))}
              </div>
            </div>
            {hasPreviewMultipleImages && <button type="button" className="prize-image-modal-nav prize-image-modal-nav-next" aria-label="下一张礼物图片" onClick={() => updatePreviewIndex(1)}>›</button>}
            {hasPreviewMultipleImages && <div className="prize-image-modal-count">{previewIndex + 1} / {previewImages.length}</div>}
          </div>
        </div>
      )}
    </div>
  );
}

export default PrizesWithCart;
